import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { AmountInput, Button, CurrenciesUSD, formatNumber } from '@column/column-ui-kit';

import { InfoForm } from '../../Transfers/_components/Layout';
import { LoanPageHeader } from '../_components/LoanPageHeader';
import { buildLoanDisbursementTimelineEvents } from '~/app/pages/Loans/Disbursement/_utils/loanTimeline';
import { ROUTE } from '~/app/routes';
import { CopyInput, Details, Loading, PageHeader, RenderFields } from '~/components';
import { useDialog } from '~/components/Dialog/Provider';
import { TimelineEvents } from '~/components/TimelineEvents';
import { useBankAccount, useEntity } from '~/hooks';
import { useNavigate } from '~/lib/navigation';
import { Loan, LoanDisbursement, LoanRepository } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';

interface Params {
  id: string;
}

interface ExtendedLoanDisbursement extends LoanDisbursement {
  createdAt?: string;
  effectiveAt?: string;
  idempotencyKey?: string;
}

const AmountInputContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;

  > label {
    width: 100%;
  }
`;

const formatData = (disbursement: ExtendedLoanDisbursement): Record<string, unknown> => {
  const formatted: Record<string, unknown> = {};
  Object.entries(disbursement).forEach(([key, value]) => {
    formatted[key] = value;
  });
  return formatted;
};

export const PageLoansDisbursementEdit: FC = () => {
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const { isAuthorized } = useSessionStore();
  const navigate = useNavigate();
  const { openAlert } = useDialog();
  const { id } = useParams<keyof Params>() as Params;

  const {
    response: bankAccount,
    isLoading: isBankAccountLoading,
    createRequest: fetchBankAccount,
  } = useBankAccount({
    onError: (error) => {
      addDangerNotification({ content: error.message });
      navigate(ROUTE.LOANS);
    },
  });

  const { response: entity, createRequest: fetchEntity } = useEntity({
    onError: (error) => {
      addDangerNotification({ content: error.message });
    },
  });

  const [disbursement, setDisbursement] = useState<ExtendedLoanDisbursement | undefined>();
  const [loan, setLoan] = useState<Loan | undefined>();
  const [isDisbursementLoading, setIsDisbursementLoading] = useState(true);
  const hasFetchedRef = useRef<string | null>(null);
  const [amountValue, setAmountValue] = useState<number | undefined>();
  const [originalAmount, setOriginalAmount] = useState<number | undefined>();

  interface DisbursementForm {
    id?: string;
    description?: string;
    status?: string;
    bankAccountId?: string;
    accountNumberId?: string;
    createdAt?: string;
    effectiveAt?: string;
    amount?: number;
    idempotencyKey?: string;
  }

  const formMethods = useForm<DisbursementForm>();

  const handleCancel = () => {
    openAlert({
      headline: 'Cancel Disbursement?',
      content: 'This will cancel the disbursement and return the funds to the loan. This action cannot be undone.',
      type: 'danger',
      confirmButton: {
        label: 'Cancel Disbursement',
        variant: 'danger',
      },
      onSuccess: () => {
        LoanRepository.cancelDisbursement(id)
          .then(() => {
            fetchDisbursement();
            addSuccessNotification({
              content: 'Disbursement cancelled',
            });
          })
          .catch((error: any) => {
            addDangerNotification({
              content: error.message,
            });
          });
      },
    });
  };

  const handleClear = () => {
    openAlert({
      headline: 'Clear disbursement hold?',
      content: 'This will clear the disbursement hold and complete the transaction.',
      confirmButton: {
        label: 'Clear Hold',
        variant: 'secondary-danger',
      },
      onSuccess: () => {
        LoanRepository.clearDisbursement(id, {})
          .then(() => {
            fetchDisbursement();
            addSuccessNotification({
              content: 'Disbursement cleared',
            });
          })
          .catch((error: any) => {
            addDangerNotification({
              content: error.message,
            });
          });
      },
    });
  };

  const handleUpdateAmount = () => {
    if (!disbursement || !amountValue || amountValue === originalAmount) {
      return;
    }

    LoanRepository.updateDisbursement(id, {
      amount: Math.round(amountValue).toString(),
      currency: disbursement.currency || 'USD',
    })
      .then(() => {
        fetchDisbursement();
        addSuccessNotification({
          content: 'Disbursement updated',
        });
      })
      .catch((error: any) => {
        addDangerNotification({
          content: error.message,
        });
      });
  };

  const fetchDisbursement = () => {
    if (!id || hasFetchedRef.current === id) return;

    hasFetchedRef.current = id;
    setDisbursement(undefined);
    setLoan(undefined);
    setIsDisbursementLoading(true);
    LoanRepository.getDisbursement(id)
      .then((response) => {
        setDisbursement(response as ExtendedLoanDisbursement);

        if (response.bankAccountId) {
          fetchBankAccount({ id: response.bankAccountId })
            .then((currentBankAccount) => {
              if (!currentBankAccount) {
                return;
              }

              if (isAuthorized({ permission: 'entities', permissionLevel: 'read' }) && currentBankAccount.owners?.[0]) {
                fetchEntity({ id: currentBankAccount.owners[0] });
              }
            })
            .catch((error) => {
              addDangerNotification({ content: (error as Error).message });
            });
        }

        if (response.loanId) {
          LoanRepository.get(response.loanId)
            .then((loanResponse) => {
              setLoan(loanResponse);
            })
            .catch((error) => {
              addDangerNotification({ content: error.message });
            });
        }
      })
      .catch((error) => {
        hasFetchedRef.current = null;
        addDangerNotification({ content: error.message });
        navigate(ROUTE.LOANS);
      })
      .finally(() => {
        setIsDisbursementLoading(false);
      });
  };

  useEffect(() => {
    fetchDisbursement();
  }, [id]);

  const formattedDisbursement = useMemo(() => (disbursement ? formatData(disbursement) : undefined), [disbursement]);

  useEffect(() => {
    if (disbursement) {
      formMethods.setValue('description', disbursement.description || '-');
      formMethods.setValue('status', disbursement.status || '-');
      if (disbursement.amount) {
        const amountInCents =
          typeof disbursement.amount === 'string' ? parseFloat(disbursement.amount) : disbursement.amount;
        formMethods.setValue('amount', amountInCents);
        setAmountValue(amountInCents);
        setOriginalAmount(amountInCents);
      }
      if ('idempotencyKey' in disbursement) {
        formMethods.setValue('idempotencyKey', (disbursement as any).idempotencyKey || '-');
      }
    }
  }, [disbursement, formMethods]);

  const isDataLoading = useMemo(() => {
    return isBankAccountLoading || isDisbursementLoading;
  }, [isBankAccountLoading, isDisbursementLoading]);

  return (
    <>
      <PageHeader text="Disbursements" backButton>
        {disbursement?.status === 'hold' && (
          <>
            <Button variant="secondary-danger" onClick={handleClear}>
              Clear Hold
            </Button>
            <Button variant="danger" onClick={handleCancel}>
              Cancel
            </Button>
          </>
        )}
      </PageHeader>

      <Loading isLoading={isDataLoading} fullHeight>
        {disbursement && (
          <LoanPageHeader
            amount={disbursement.amount!}
            paymentDate={disbursement.effectiveAt || disbursement.createdAt}
            status={disbursement.status!}
            loanId={disbursement.id!}
            loanType="disbursement"
            currencyCode={disbursement.currency}
          />
        )}
        {bankAccount && disbursement && loan && (
          <TimelineEvents
            entries={buildLoanDisbursementTimelineEvents({
              disbursement,
              loan,
              bankAccount,
              entityType:
                entity?.type && entity.type !== 'TYPE_NONE'
                  ? (entity.type.toLowerCase() as 'person' | 'business')
                  : undefined,
              navigate,
            })}
          />
        )}
        <FormProvider {...formMethods}>
          <InfoForm autoComplete="off">
            {RenderFields<DisbursementForm>({
              sections: [
                {
                  headline: 'Information',
                  fields: [
                    {
                      id: 'description',
                      label: 'Description',
                      children: ({ value }) => {
                        return <CopyInput value={String(value || '-')} />;
                      },
                    },
                    {
                      id: 'amount',
                      label: 'Amount',
                      children: ({ value }) => {
                        const numValue = typeof value === 'string' ? parseFloat(value) : value;
                        const isAmountChanged =
                          amountValue !== undefined &&
                          originalAmount !== undefined &&
                          Math.abs(amountValue - originalAmount) > 0.001;
                        const canEdit = disbursement?.status === 'hold';

                        if (!canEdit) {
                          return <CopyInput value={formatNumber(numValue ?? 0)} />;
                        }

                        return (
                          <AmountInputContainer>
                            <AmountInput
                              value={amountValue ?? numValue}
                              currencyCode={disbursement?.currency}
                              currencyList={CurrenciesUSD}
                              readOnly={!canEdit}
                              onChange={(newValue) => {
                                setAmountValue(newValue);
                                formMethods.setValue('amount', newValue);
                              }}
                            />
                            {isAmountChanged && canEdit && <Button onClick={handleUpdateAmount}>Update</Button>}
                          </AmountInputContainer>
                        );
                      },
                    },
                    {
                      id: 'idempotencyKey',
                      label: 'Idempotency Key',
                      children: ({ value }) => {
                        return <CopyInput value={String(value || '')} />;
                      },
                    },
                  ],
                },
              ],
            })}
            <Details
              label="Loan Disbursement Details"
              formattedData={formattedDisbursement}
              rawData={disbursement as Record<string, unknown> | undefined}
            />
          </InfoForm>
        </FormProvider>
      </Loading>
    </>
  );
};
